import React, { useState, useEffect } from 'react';
import { useConsent, CONSENT_TYPES } from '../contexts/ConsentContext';

function PrivacySettings() {
  const { consent, updateConsent, resetConsent } = useConsent();
  const [isOpen, setIsOpen] = useState(false);
  const [tempConsent, setTempConsent] = useState(consent);

  // Synchronize tempConsent with consent when it changes (e.g., loaded from cookies)
  useEffect(() => {
    setTempConsent(consent);
  }, [consent]);

  const handleToggleConsent = (type) => {
    if (type === CONSENT_TYPES.NECESSARY) return;
    
    setTempConsent(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleSave = () => {
    updateConsent(tempConsent);
    setIsOpen(false);
  };

  const handleReset = () => {
    resetConsent();
    setIsOpen(false);
  };

  const handleOpenSettings = () => {
    setTempConsent(consent); // Ensure we have the latest consent values
    setIsOpen(true);
  };

  const consentDescriptions = {
    [CONSENT_TYPES.NECESSARY]: {
      title: 'Niezbędne',
      description: 'Te pliki cookie są konieczne do prawidłowego funkcjonowania strony internetowej.'
    },
    [CONSENT_TYPES.ANALYTICS]: {
      title: 'Analityczne',
      description: 'Pomagają nam zrozumieć, jak odwiedzający korzystają z naszej strony.'
    },
    [CONSENT_TYPES.MARKETING]: {
      title: 'Marketingowe',
      description: 'Używane do wyświetlania spersonalizowanych reklam.'
    },
    [CONSENT_TYPES.PREFERENCES]: {
      title: 'Preferencje',
      description: 'Pozwalają zapamiętać Twoje preferencje i ustawienia.'
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={handleOpenSettings}
        className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-700 transition-colors z-40"
      >
        Ustawienia prywatności
      </button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Ustawienia prywatności
            </h2>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4 mb-6">
            {Object.entries(CONSENT_TYPES).map(([key, type]) => (
              <div key={type} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900 mb-1">
                      {consentDescriptions[type].title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {consentDescriptions[type].description}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      checked={tempConsent[type]}
                      onChange={() => handleToggleConsent(type)}
                      disabled={type === CONSENT_TYPES.NECESSARY}
                      className="sr-only peer"
                    />
                    <div className={`relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 ${
                      type === CONSENT_TYPES.NECESSARY ? 'opacity-50 cursor-not-allowed' : ''
                    }`}></div>
                  </label>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleSave}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Zapisz zmiany
            </button>
            <button
              onClick={handleReset}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Resetuj ustawienia
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-600 hover:text-gray-800 px-6 py-2 text-sm font-medium transition-colors"
            >
              Anuluj
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacySettings;
